/*
Andreas Arvidsson
https://www.w3.org/TR/wai-aria-practices-1.1/examples/listbox/listbox-scrollable.html
*/

import type { TargetedKeyboardEvent, TargetedMouseEvent, VNode } from "preact";
import { forwardRef } from "preact/compat";
import type { ForwardedRef } from "preact/compat";
import { useEffect, useRef, useState } from "preact/hooks";
import "./List.css";
import { classNames } from "@gss/common/classNames";
import { useConst } from "@gss/hooks/useConst";

let nextId = 1;

interface Props {
    className?: string;
    onSelect: (index: number, key: string) => void;
    children: VNode[];
    disabled?: boolean;
}

export const ListBox = forwardRef(
    (
        { className, disabled, children, onSelect, ...rest }: Props,
        fRef: ForwardedRef<HTMLUListElement>,
    ) => {
        const [active, setActive] = useState<VNode>();
        const id = useConst(() => `list-box-${nextId++}`);
        const [search, setSearch] = useState({ value: "", time: 0 });
        const localRef = useRef<HTMLUListElement>(null);
        const ref = fRef ?? localRef;
        const activeId = `${id}-active`;

        useEffect(() => {
            setActive(children[0]);
        }, [children]);

        // Search for element with search text in name
        useEffect(() => {
            const c = children.find((c) => {
                const label = String(c.props.children).toLowerCase();
                return label.startsWith(search.value);
            });
            if (c) {
                setActive(c);
            }
        }, [search]);

        const moveActive = (diff: number | "Home" | "End") => {
            let c;
            if (diff === "Home") {
                c = children[0];
            } else if (diff === "End") {
                c = children[children.length - 1];
            } else if (active != null) {
                const i = children.indexOf(active) + diff;
                c = children[i];
            }
            if (c && c !== active) {
                setActive(c);
            }
        };

        const onKeyDown = (e: TargetedKeyboardEvent<HTMLUListElement>) => {
            switch (e.key) {
                case "ArrowUp":
                    moveActive(-1);
                    break;
                case "ArrowDown":
                    moveActive(1);
                    break;
                case "Home":
                    moveActive(e.key);
                    break;
                case "End":
                    moveActive(e.key);
                    break;
                case " ":
                case "Enter": {
                    if (active != null) {
                        const i = children.indexOf(active);
                        onSelect(i, e.key);
                    }
                    break;
                }
                default:
                    if (e.key.length === 1) {
                        const key = e.key.toLowerCase();
                        const time = Date.now();
                        setSearch({
                            time,
                            value:
                                time - search.time > 1000
                                    ? key
                                    : search.value + key,
                        });
                    } else {
                        // Important so we don't run preventDefault() for unknown keys
                        return;
                    }
            }
            e.preventDefault();
        };

        const onMouseOver = (c: VNode) => {
            if (c !== active) {
                setActive(c);
            }
        };

        const onClickFocus = (e: TargetedMouseEvent<HTMLUListElement>) => {
            localRef.current?.focus();
            e.stopPropagation();
        };

        return (
            <ul
                ref={ref}
                role="listbox"
                aria-activedescendant={activeId}
                className={classNames("list-box", className)}
                tabIndex={disabled ? -1 : 0}
                onClick={disabled ? undefined : onClickFocus}
                onKeyDown={disabled ? undefined : onKeyDown}
                {...rest}
            >
                {children.map((c, i) => {
                    const isActive = c === active;
                    return (
                        <li
                            key={i}
                            id={isActive ? activeId : undefined}
                            role="option"
                            onMouseOver={
                                disabled ? undefined : () => onMouseOver(c)
                            }
                        >
                            {c}
                        </li>
                    );
                })}
            </ul>
        );
    },
);

ListBox.displayName = "ListBox";
